import React from 'react';
import { Box } from '@mui/material';
import { theme, ThemedTypography, colors, spacing, borderRadius, shadows, typography } from '../../../themes/technicalAlerts';
import { createFilterDropdown, FILTER_CONFIG } from '../filterConfig';
import { UnifiedDropdown } from '../Shared/DropdownComponents';

/**
 * Filter control for table and cards view
 * Provides dropdown interface for filtering data by various columns
 * Extracted from TechnicalAlertsTable.jsx for better component separation
 */
const FilterControl = ({
  state,
  showFilterDropdown,
  selectedFilterColumn,
  onToggleFilterDropdown,
  onSetSelectedFilterColumn,
  filterData,
  filtersLoading,
  refetch,
  columnConfigs,
  t
}) => {
  // No intermediate state needed - let dropdown manage its own state

  const hasActiveFilters = Object.keys(state.table.filteredInfo).some(
    key => state.table.filteredInfo[key]?.length > 0
  );
  
  const activeFilterCount = Object.keys(state.table.filteredInfo).filter(
    key => state.table.filteredInfo[key]?.length > 0
  ).length;

  const handleToggleClick = () => {
    onToggleFilterDropdown();
  };

  return (
    <UnifiedDropdown
      data-filter-control
      type="custom"
      isOpen={showFilterDropdown}
      isActive={hasActiveFilters}
      onClick={handleToggleClick}
      onClose={onToggleFilterDropdown}
      icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path 
            d="M3 4.6C3 4.03995 3 3.75992 3.10899 3.54601C3.20487 3.35785 3.35785 3.20487 3.54601 3.10899C3.75992 3 4.03995 3 4.6 3H19.4C19.9601 3 20.2401 3 20.454 3.10899C20.6422 3.20487 20.7951 3.35785 20.891 3.54601C21 3.75992 21 4.03995 21 4.6V6.33726C21 6.58185 21 6.70414 20.9724 6.81923C20.9479 6.92127 20.9075 7.01881 20.8526 7.10828C20.7908 7.2092 20.7043 7.29568 20.5314 7.46863L14.4686 13.5314C14.2957 13.7043 14.2092 13.7908 14.1474 13.8917C14.0925 13.9812 14.0521 14.0787 14.0276 14.1808C14 14.2959 14 14.4182 14 14.6627V17L10 21V14.6627C10 14.4182 10 14.2959 9.97237 14.1808C9.94787 14.0787 9.90747 13.9812 9.85264 13.8917C9.7908 13.7908 9.70432 13.7043 9.53137 13.5314L3.46863 7.46863C3.29568 7.29568 3.2092 7.2092 3.14736 7.10828C3.09253 7.01881 3.05213 6.92127 3.02763 6.81923C3 6.70414 3 6.58185 3 6.33726V4.6Z" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      }
      label={t('filter')}
      activeCount={hasActiveFilters ? activeFilterCount : 0}
      sx={{ gap: '6px' }}
    >

      {/* Compact Filter Dropdown */}
      <Box 
        data-filter-control
        sx={{
          backgroundColor: colors.background.white,
          borderRadius: borderRadius.lg,
          boxShadow: shadows.popover,
          overflow: 'hidden',
          minWidth: '320px'
        }}>
          {!selectedFilterColumn ? (
            // Column Selection View
            <>
              <Box sx={{ px: spacing.sm, py: spacing.sm, borderBottom: `1px solid ${colors.border.light}` }}>
                <ThemedTypography.H5>
                  {t('filterBy')}
                </ThemedTypography.H5>
              </Box>
              
              {Object.entries(columnConfigs).map(([key, config]) => {
                const availableValues = filterData?.[FILTER_CONFIG[key]?.serverKey] || [];
                const isActive = state.table.filteredInfo[key]?.length > 0;
                
                return (
                  <Box
                    key={key}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetSelectedFilterColumn(key);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: spacing.sm,
                      py: spacing.sm,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: colors.background.hover
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {isActive && (
                        <Box sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: colors.primary,
                          flexShrink: 0
                        }} />
                      )}
                      <ThemedTypography.Body2 sx={{
                        color: isActive ? colors.primaryHover : colors.text.primary
                      }}>
                        {config.title}
                      </ThemedTypography.Body2>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ThemedTypography.Body3 sx={{
                        color: colors.text.hint
                      }}>
                        {availableValues.length}
                      </ThemedTypography.Body3>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path 
                          d="M9 18L15 12L9 6" 
                          stroke={colors.text.hint} 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Box>
                  </Box>
                );
              })}
            </>
          ) : (
            // Filter Values View - Use exact same pattern as table
            <Box onClick={(e) => e.stopPropagation()}>
              {(() => {
                // Create the exact same filter dropdown as used in the table
                const FilterDropdownComponent = createFilterDropdown(
                  selectedFilterColumn,
                  filterData,
                  FILTER_CONFIG[selectedFilterColumn]?.translationKey,
                  filtersLoading,
                  t
                );
                
                return (
                  <FilterDropdownComponent
                    selectedKeys={state.table.filteredInfo[selectedFilterColumn] || []}
                    setSelectedKeys={() => {
                      // No-op - dropdown manages its own state internally
                    }}
                    confirm={(finalKeys) => {
                      // Apply the final selections directly
                      state.table.setFilteredInfo(prev => ({
                        ...prev,
                        [selectedFilterColumn]: finalKeys && finalKeys.length > 0 ? finalKeys : undefined
                      }));
                      refetch();
                      // Go back to column selection view for easy multi-column filtering
                      onSetSelectedFilterColumn(null);
                      // Close the main filter dropdown
                      onToggleFilterDropdown();
                    }}
                    clearFilters={() => {
                      // Clear actual filter state
                      state.table.setFilteredInfo(prev => {
                        const newFilters = { ...prev };
                        delete newFilters[selectedFilterColumn];
                        return newFilters;
                      });
                      refetch();
                      onSetSelectedFilterColumn(null);
                      // Close the main filter dropdown
                      onToggleFilterDropdown();
                    }}
                  />
                );
              })()}
            </Box>
          )}
        </Box>
      
    </UnifiedDropdown>
  );
};

export default FilterControl;